import { useCallback, useEffect, useRef, useState } from 'react';

const JITTER_THRESHOLD_PX = 5;

/**
 * Observes the rendered matrix container width for vision-engine gear-shifting.
 * Returns undefined until the first ResizeObserver measurement.
 * Suppresses updates smaller than 5px to avoid micro-jitter re-solves.
 */
export function useMatrixContainerWidth(): {
  containerRef: (node: HTMLDivElement | null) => void;
  containerWidth: number | undefined;
} {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>();
  const prevWidthRef = useRef<number | undefined>();

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width != null && width > 0) {
        const prev = prevWidthRef.current;
        if (prev == null || Math.abs(width - prev) > JITTER_THRESHOLD_PX) {
          prevWidthRef.current = width;
          setContainerWidth(width);
        }
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { containerRef, containerWidth };
}
